import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import {
    Plus, Building2, MapPin, Home, TrendingUp, Users, Zap,
    ArrowLeft, Edit3, Layers, DollarSign, ChevronRight,
    FileText, Zap as ZapIcon, AlertCircle, TrendingDown,
} from 'lucide-react';
import { PageHeader, Card, StatTile, Badge, EmptyState } from '../components/ui';
import ChargeBreakdown from '../components/charges/ChargeBreakdown';
import UnitCharges from '../components/charges/UnitCharges';
import { AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

/* Route: /buildings and /buildings/:id */
const Buildings = () => {
    const { id } = useParams();
    return id ? <BuildingDetail id={id} /> : <BuildingList />;
};

/* ============================================================
 * LIST
 * ============================================================ */
const BuildingList = () => {
    const [buildings, setBuildings] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [showForm, setShowForm] = useState(false);

    const load = async () => {
        setLoading(true);
        try { const r = await axios.get('/api/buildings'); setBuildings(r.data.buildings || []); }
        catch { toast.error('Failed to load buildings'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const totals = React.useMemo(() => buildings.reduce((acc, b) => ({
        buildings: acc.buildings + 1,
        units: acc.units + Number(b.units_count || 0),
        occupied: acc.occupied + Number(b.occupied_units || 0),
        income: acc.income + Number(b.actual_income || 0),
        potential: acc.potential + Number(b.potential_income || 0),
    }), { buildings: 0, units: 0, occupied: 0, income: 0, potential: 0 }), [buildings]);

    return (
        <div className="animate-fade-in">
            <PageHeader
                title="Buildings"
                subtitle="Multi-unit buildings and their per-unit occupancy."
                actions={
                    <button onClick={() => setShowForm(true)} className="btn-primary">
                        <Plus className="h-4 w-4" /> New building
                    </button>
                }
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <StatTile label="Buildings"  value={totals.buildings} icon={Building2} accent="primary" />
                <StatTile label="Units"      value={totals.units}     icon={Layers}    accent="primary"
                          hint={`${totals.occupied} occupied · ${totals.units - totals.occupied} available`} />
                <StatTile label="Occupancy"  value={totals.units ? `${Math.round(totals.occupied / totals.units * 100)}%` : '—'}
                          icon={Home} accent="success" hint="Across all buildings" />
                <StatTile label="Realised / Potential income"
                          value={formatCurrency(totals.income)} icon={DollarSign} accent="primary"
                          hint={`of ${formatCurrency(totals.potential)}/mo potential`} />
            </div>

            {loading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1,2,3,4].map(i => <div key={i} className="h-48 bg-white rounded-xl border border-neutral-200/70 animate-pulse-soft" />)}
                </div>
            ) : buildings.length === 0 ? (
                <EmptyState
                    icon={Building2}
                    title="No buildings yet"
                    description="Create a building to group multiple units under one address — great for apartment blocks and townhouses. Units still get their own rent, size, and tenants."
                    action={<button onClick={() => setShowForm(true)} className="btn-primary">
                        <Plus className="h-4 w-4" /> Create your first building
                    </button>}
                />
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {buildings.map(b => <BuildingCard key={b.id} b={b} />)}
                </div>
            )}

            {showForm && <BuildingForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
        </div>
    );
};

const BuildingCard = ({ b }) => {
    const rate = b.units_count > 0 ? Math.round(Number(b.occupied_units) / Number(b.units_count) * 100) : 0;
    return (
        <Link to={`/buildings/${b.id}`} className="card card-hover p-5 block animate-slide-up">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="font-semibold text-neutral-900 truncate">{b.name}</h3>
                    <p className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="h-3 w-3" /> {b.city}
                    </p>
                </div>
                <Badge tone="primary">{b.building_type?.replace('_', ' ')}</Badge>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div>
                    <div className="text-xs text-neutral-500">Units</div>
                    <div className="text-lg font-semibold text-neutral-900 tabular-nums">{b.units_count || 0}</div>
                </div>
                <div>
                    <div className="text-xs text-neutral-500">Occupied</div>
                    <div className="text-lg font-semibold text-primary-700 tabular-nums">{b.occupied_units || 0}</div>
                </div>
                <div>
                    <div className="text-xs text-neutral-500">Rate</div>
                    <div className="text-lg font-semibold text-neutral-900 tabular-nums">{rate}%</div>
                </div>
            </div>

            <div className="mt-3 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                <div className="h-full bg-primary-600" style={{ width: `${rate}%` }} />
            </div>

            <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center justify-between">
                <div>
                    <div className="text-xs text-neutral-500">Actual / Potential (mo)</div>
                    <div className="text-sm font-semibold text-neutral-900 tabular-nums">
                        {formatCurrency(b.actual_income)}
                        <span className="text-neutral-400 font-normal"> / {formatCurrency(b.potential_income)}</span>
                    </div>
                </div>
                <ChevronRight className="h-4 w-4 text-neutral-400" />
            </div>
        </Link>
    );
};

/* ============================================================
 * DETAIL — one building + its units + analysis
 * ============================================================ */
const BuildingDetail = ({ id }) => {
    const [data, setData] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showBulk, setShowBulk] = useState(false);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [showExpenseForm, setShowExpenseForm] = useState(false);
    const [showUtilityForm, setShowUtilityForm] = useState(false);
    const [showOwnerReport, setShowOwnerReport] = useState(false);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const [r1, r2] = await Promise.all([
                axios.get(`/api/buildings/${id}`),
                axios.get(`/api/buildings/${id}/analysis`),
            ]);
            setData(r1.data);
            setAnalysis(r2.data);
        } catch (err) {
            toast.error('Failed to load');
            navigate('/buildings');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [id]); // eslint-disable-line

    if (loading || !data) return <div className="p-8 text-center text-neutral-500">Loading…</div>;

    const b = data.building;
    const units = data.units || [];
    const occ = units.filter(u => u.status === 'occupied').length;
    const potential = units.reduce((s, u) => s + Number(u.rent_amount || 0), 0);
    const actual = units.filter(u => u.status === 'occupied').reduce((s, u) => s + Number(u.rent_amount || 0), 0);
    const vacant = units.filter(u => u.status === 'available');

    // Expense categories for pie chart
    const expenseChartData = analysis && Object.entries(analysis.misc_expenses || {}).map(([cat, amt]) => ({
        name: cat, value: Number(amt)
    }));
    const expenseColors = ['#0f766e', '#14b8a6', '#2dd4bf', '#67e8f9', '#a5f3fc'];

    return (
        <div className="animate-fade-in">
            <div className="mb-4">
                <Link to="/buildings" className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1">
                    <ArrowLeft className="h-3 w-3" /> All buildings
                </Link>
            </div>
            <PageHeader
                title={b.name}
                subtitle={`${b.address}, ${b.city}${b.zip_code ? ' ' + b.zip_code : ''} · ${b.building_type?.replace('_', ' ')}`}
                actions={
                    <div className="flex flex-wrap gap-2">
                        <button onClick={() => setShowBulk(true)} className="btn-secondary">
                            <Layers className="h-4 w-4" /> Bulk add units
                        </button>
                        <Link to={`/properties?building_id=${b.id}`} className="btn-secondary">
                            <Plus className="h-4 w-4" /> Add unit
                        </Link>
                        <button onClick={() => setShowExpenseForm(true)} className="btn-secondary">
                            <TrendingDown className="h-4 w-4" /> Add expense
                        </button>
                        <button onClick={() => setShowUtilityForm(true)} className="btn-secondary">
                            <ZapIcon className="h-4 w-4" /> Add utility
                        </button>
                        <button onClick={() => setShowOwnerReport(true)} className="btn-secondary">
                            <FileText className="h-4 w-4" /> Owner report
                        </button>
                    </div>
                }
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <StatTile label="Units" value={units.length} icon={Layers} accent="primary" />
                <StatTile label="Occupied" value={occ} icon={Home} accent="success"
                    hint={units.length ? `${Math.round(occ / units.length * 100)}% occupancy` : '—'} />
                <StatTile label="Potential income"
                    value={formatCurrency(potential)} icon={TrendingUp} accent="primary" hint="If all units occupied" />
                <StatTile label="Actual income"
                    value={formatCurrency(actual)} icon={DollarSign} accent="primary" hint="Currently earning /mo" />
            </div>

            {/* FINANCIAL ANALYSIS */}
            {analysis && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                        <StatTile
                            label="Income (This Month)"
                            value={formatCurrency(analysis.income_mtd)}
                            icon={TrendingUp}
                            accent="primary"
                        />
                        <StatTile
                            label="Income (YTD)"
                            value={formatCurrency(analysis.income_ytd)}
                            icon={DollarSign}
                            accent="primary"
                        />
                        <StatTile
                            label="Unpaid Rent"
                            value={formatCurrency(analysis.unpaid_rent)}
                            icon={AlertCircle}
                            accent={analysis.unpaid_rent > 0 ? 'warning' : 'neutral'}
                        />
                        <StatTile
                            label="Overdue Rent"
                            value={formatCurrency(analysis.overdue_rent)}
                            icon={AlertCircle}
                            accent={analysis.overdue_rent > 0 ? 'danger' : 'neutral'}
                        />
                    </div>

                    {/* TREND CHART */}
                    {analysis.trend && analysis.trend.length > 0 && (
                        <Card title="6-Month Cash Flow" subtitle="Income vs Expenses" className="mb-6">
                            <div className="p-4">
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart data={analysis.trend} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#0f766e" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#0f766e" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#dc2626" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey="label" stroke="#9ca3af" style={{ fontSize: '0.75rem' }} />
                                        <YAxis stroke="#9ca3af" style={{ fontSize: '0.75rem' }} />
                                        <Tooltip
                                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}
                                            formatter={(v) => formatCurrency(v)}
                                        />
                                        <Legend />
                                        <Area type="monotone" dataKey="income" stroke="#0f766e" strokeWidth={2} fill="url(#colorIncome)" name="Income" />
                                        <Area type="monotone" dataKey="expenses" stroke="#dc2626" strokeWidth={2} fill="url(#colorExpenses)" name="Expenses" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                    )}

                    {/* EXPENSES BY CATEGORY */}
                    {expenseChartData && expenseChartData.length > 0 && (
                        <Card title="Expenses by Category (YTD)" subtitle={`Total: ${formatCurrency(analysis.expenses_ytd)}`} className="mb-6">
                            <div className="p-4">
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={expenseChartData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                                            outerRadius={100}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {expenseChartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={expenseColors[index % expenseColors.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(v) => formatCurrency(v)} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                    )}
                </>
            )}

            {/* UNITS TABLE */}
            <Card title="Units in this building"
                subtitle={`${units.length} unit${units.length !== 1 ? 's' : ''}`}
                padding={false}
                className="mb-6">
                {units.length === 0 ? (
                    <div className="p-8">
                        <EmptyState icon={Home}
                            title="No units yet"
                            description="Add units one at a time, or use bulk-add to create a numbered series (Unit 1, Unit 2, …)."
                            action={
                                <div className="flex gap-2 justify-center">
                                    <button onClick={() => setShowBulk(true)} className="btn-secondary">Bulk add</button>
                                    <Link to={`/properties?building_id=${b.id}`} className="btn-primary">Add unit</Link>
                                </div>
                            } />
                    </div>
                ) : (
                    <div className="w-full overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-neutral-50 text-left border-b border-neutral-200/70">
                                <tr>
                                    <th className="py-3 px-4 font-medium text-xs uppercase tracking-wide text-neutral-500">Unit</th>
                                    <th className="py-3 px-4 font-medium text-xs uppercase tracking-wide text-neutral-500">Type</th>
                                    <th className="py-3 px-4 font-medium text-xs uppercase tracking-wide text-neutral-500 text-center">Beds / Baths</th>
                                    <th className="py-3 px-4 font-medium text-xs uppercase tracking-wide text-neutral-500 text-right">Sq ft</th>
                                    <th className="py-3 px-4 font-medium text-xs uppercase tracking-wide text-neutral-500 text-right">Rent</th>
                                    <th className="py-3 px-4 font-medium text-xs uppercase tracking-wide text-neutral-500">Status</th>
                                    <th className="py-3 px-4 font-medium text-xs uppercase tracking-wide text-neutral-500 text-center">Tenants</th>
                                    <th className="py-3 px-4" />
                                </tr>
                            </thead>
                            <tbody>
                                {units.map(u => (
                                    <tr key={u.id} className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 cursor-pointer"
                                        onClick={() => setSelectedUnit(u)}>
                                        <td className="py-3 px-4">
                                            <div className="font-medium text-neutral-900">
                                                {u.house_number && <span className="text-primary-700 mr-1">#{u.house_number}</span>}
                                                {u.name}
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 capitalize text-neutral-600">{u.property_type}</td>
                                        <td className="py-3 px-4 text-center text-neutral-600 tabular-nums">
                                            {u.bedrooms ?? '—'} / {u.bathrooms ?? '—'}
                                        </td>
                                        <td className="py-3 px-4 text-right tabular-nums text-neutral-600">
                                            {u.square_feet ? Number(u.square_feet).toLocaleString() : '—'}
                                        </td>
                                        <td className="py-3 px-4 text-right tabular-nums font-medium text-neutral-900">
                                            {formatCurrency(u.rent_amount)}
                                        </td>
                                        <td className="py-3 px-4">
                                            <Badge tone={
                                                u.status === 'occupied' ? 'success' :
                                                u.status === 'available' ? 'neutral' :
                                                u.status === 'maintenance' ? 'warning' : 'neutral'
                                            } dot>{u.status}</Badge>
                                        </td>
                                        <td className="py-3 px-4 text-center tabular-nums text-neutral-600">
                                            {u.active_tenants || 0}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <ChevronRight className="h-4 w-4 text-neutral-300" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {/* ADD TENANT TO VACANT UNIT */}
            {vacant.length > 0 && (
                <Card title="Add Tenant to Vacant Unit" subtitle={`${vacant.length} unit${vacant.length !== 1 ? 's' : ''} available`} className="mb-6">
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                        {vacant.map(u => (
                            <Link
                                key={u.id}
                                to={`/tenants?property_id=${u.id}`}
                                className="p-3 border border-neutral-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                            >
                                <div className="font-medium text-neutral-900">
                                    {u.house_number && <span className="text-primary-700 mr-1">#{u.house_number}</span>}
                                    {u.name}
                                </div>
                                <p className="text-xs text-neutral-500 mt-1">{formatCurrency(u.rent_amount)}/mo</p>
                            </Link>
                        ))}
                    </div>
                </Card>
            )}

            {showBulk && <BulkUnitsForm building={b} onClose={() => setShowBulk(false)} onDone={() => { setShowBulk(false); load(); }} />}
            {showExpenseForm && <ExpenseForm building={b} onClose={() => setShowExpenseForm(false)} onSaved={() => { setShowExpenseForm(false); load(); }} />}
            {showUtilityForm && <UtilityForm building={b} onClose={() => setShowUtilityForm(false)} onSaved={() => { setShowUtilityForm(false); load(); }} />}
            {showOwnerReport && <OwnerReportModal building={b} analysis={analysis} onClose={() => setShowOwnerReport(false)} />}
            {selectedUnit && <UnitDetailModal unit={selectedUnit} onClose={() => setSelectedUnit(null)} />}
        </div>
    );
};

/* ============================================================
 * UNIT DETAIL MODAL
 * ============================================================ */
const UnitDetailModal = ({ unit, onClose }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const r = await axios.get(`/api/properties/${unit.id}/detail`);
                setDetail(r.data);
            } catch (err) {
                toast.error('Failed to load unit details');
                onClose();
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [unit.id, onClose]);

    if (loading || !detail) {
        return (
            <Modal title="Loading…" onClose={onClose}>
                <div className="p-8 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border border-primary-300 border-t-primary-600" />
                </div>
            </Modal>
        );
    }

    const { property, current_tenant, financials, payments, invoices, expenses, maintenance } = detail;

    return (
        <Modal title={unit.name} subtitle={`${property.address}, ${property.city}`} onClose={onClose} size="lg">
            <div className="p-5 overflow-y-auto max-h-[80vh]">
                {/* Current Tenant */}
                {current_tenant && (
                    <Card title="Current Tenant" padding={false} className="mb-4">
                        <div className="p-4 border-b border-neutral-100">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-neutral-500 uppercase">Name</p>
                                    <p className="text-sm font-medium text-neutral-900">
                                        {current_tenant.first_name} {current_tenant.last_name}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-neutral-500 uppercase">Email</p>
                                    <p className="text-sm font-medium text-neutral-900">{current_tenant.email}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-neutral-500 uppercase">Phone</p>
                                    <p className="text-sm font-medium text-neutral-900">{current_tenant.phone || '—'}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-neutral-500 uppercase">Lease Period</p>
                                    <p className="text-sm font-medium text-neutral-900">
                                        {new Date(current_tenant.lease_start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} to{' '}
                                        {new Date(current_tenant.lease_end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Card>
                )}

                {/* Financials */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="p-3 bg-primary-50 rounded-lg">
                        <p className="text-xs text-neutral-500">Total Billed</p>
                        <p className="text-lg font-semibold text-primary-700">{formatCurrency(financials.total_billed)}</p>
                    </div>
                    <div className="p-3 bg-success-50 rounded-lg">
                        <p className="text-xs text-neutral-500">Total Paid</p>
                        <p className="text-lg font-semibold text-success-700">{formatCurrency(financials.total_paid)}</p>
                    </div>
                    <div className="p-3 bg-warning-50 rounded-lg">
                        <p className="text-xs text-neutral-500">Outstanding</p>
                        <p className="text-lg font-semibold text-warning-700">{formatCurrency(financials.outstanding)}</p>
                    </div>
                    <div className="p-3 bg-danger-50 rounded-lg">
                        <p className="text-xs text-neutral-500">Overdue</p>
                        <p className="text-lg font-semibold text-danger-700">{formatCurrency(financials.overdue)}</p>
                    </div>
                </div>

                {/* Per-charge balances for this unit, across every tenancy it has had. */}
                <div className="mb-4">
                    <ChargeBreakdown scope="property" id={unit.id} title="Charges for this unit" />
                </div>

                {/* What this unit is charged on top of rent. */}
                <div className="mb-4">
                    <UnitCharges propertyId={unit.id} />
                </div>

                {/* Arrears Aging */}
                <Card title="Arrears Aging" padding={false} className="mb-4">
                    <div className="p-4 grid grid-cols-5 gap-2 text-center text-xs">
                        {Object.entries(financials.arrears).map(([bucket, amount]) => (
                            <div key={bucket} className="p-2 bg-neutral-50 rounded">
                                <p className="text-neutral-500 text-xs mb-1 uppercase">
                                    {bucket === 'current' ? 'Current' : bucket.replace('d', '').replace('_', '-')} days
                                </p>
                                <p className="font-semibold text-neutral-900">{formatCurrency(amount)}</p>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Payments */}
                {payments && payments.length > 0 && (
                    <Card title="Recent Payments" subtitle={`Last ${payments.length}`} padding={false} className="mb-4">
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-neutral-50 border-b border-neutral-200">
                                    <tr>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Date</th>
                                        <th className="py-2 px-3 text-right text-neutral-600 font-medium">Amount</th>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Method</th>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payments.map(p => (
                                        <tr key={p.id} className="border-b border-neutral-100 last:border-b-0">
                                            <td className="py-2 px-3">{new Date(p.payment_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                            <td className="py-2 px-3 text-right font-medium">{formatCurrency(p.amount)}</td>
                                            <td className="py-2 px-3 capitalize">{p.payment_method}</td>
                                            <td className="py-2 px-3">
                                                <Badge tone={p.status === 'completed' ? 'success' : 'neutral'} dot>
                                                    {p.status}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}

                {/* Invoices */}
                {invoices && invoices.length > 0 && (
                    <Card title="Recent Invoices" subtitle={`Last ${invoices.length}`} padding={false} className="mb-4">
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-neutral-50 border-b border-neutral-200">
                                    <tr>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Invoice #</th>
                                        <th className="py-2 px-3 text-right text-neutral-600 font-medium">Amount</th>
                                        <th className="py-2 px-3 text-right text-neutral-600 font-medium">Paid</th>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Due Date</th>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map(inv => (
                                        <tr key={inv.id} className="border-b border-neutral-100 last:border-b-0">
                                            <td className="py-2 px-3">{inv.invoice_number}</td>
                                            <td className="py-2 px-3 text-right font-medium">{formatCurrency(inv.total_amount)}</td>
                                            <td className="py-2 px-3 text-right">{formatCurrency(inv.paid_amount)}</td>
                                            <td className="py-2 px-3">{new Date(inv.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                            <td className="py-2 px-3">
                                                <Badge tone={
                                                    inv.status === 'paid' ? 'success' :
                                                    inv.status === 'overdue' ? 'danger' :
                                                    'neutral'
                                                } dot>
                                                    {inv.status}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}

                {/* Expenses */}
                {expenses && expenses.length > 0 && (
                    <Card title="Recent Expenses" subtitle={`Last ${expenses.length}`} padding={false} className="mb-4">
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-neutral-50 border-b border-neutral-200">
                                    <tr>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Date</th>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Category</th>
                                        <th className="py-2 px-3 text-left text-neutral-600 font-medium">Description</th>
                                        <th className="py-2 px-3 text-right text-neutral-600 font-medium">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expenses.map(exp => (
                                        <tr key={exp.id} className="border-b border-neutral-100 last:border-b-0">
                                            <td className="py-2 px-3">{new Date(exp.expense_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                            <td className="py-2 px-3 capitalize">{exp.category}</td>
                                            <td className="py-2 px-3">{exp.description}</td>
                                            <td className="py-2 px-3 text-right font-medium">{formatCurrency(exp.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}
            </div>
        </Modal>
    );
};

/* ============================================================
 * OWNER REPORT MODAL
 * ============================================================ */
const OwnerReportModal = ({ building, analysis, onClose }) => (
    <Modal title={`Owner Report — ${building.name}`} onClose={onClose} size="lg">
        <div className="p-5">
            <div className="grid grid-cols-2 gap-4 mb-6">
                <Card padding={false}>
                    <div className="p-4">
                        <p className="text-xs text-neutral-500 uppercase mb-1">Building Address</p>
                        <p className="text-sm font-medium text-neutral-900">{building.address}</p>
                        <p className="text-xs text-neutral-600 mt-1">{building.city}{building.zip_code ? ' ' + building.zip_code : ''}</p>
                    </div>
                </Card>
                <Card padding={false}>
                    <div className="p-4">
                        <p className="text-xs text-neutral-500 uppercase mb-1">Building Type</p>
                        <p className="text-sm font-medium text-neutral-900">{building.building_type?.replace('_', ' ')}</p>
                        <p className="text-xs text-neutral-600 mt-1">Generated {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                    </div>
                </Card>
            </div>

            {analysis && (
                <>
                    <Card title="Financial Summary" padding={false} className="mb-4">
                        <div className="p-4 space-y-3">
                            <div className="flex justify-between items-end">
                                <span className="text-sm text-neutral-600">YTD Income</span>
                                <span className="text-lg font-semibold text-neutral-900">{formatCurrency(analysis.income_ytd)}</span>
                            </div>
                            <div className="flex justify-between items-end">
                                <span className="text-sm text-neutral-600">YTD Expenses</span>
                                <span className="text-lg font-semibold text-neutral-900">{formatCurrency(analysis.expenses_ytd)}</span>
                            </div>
                            <div className="border-t border-neutral-200 pt-3 flex justify-between items-end">
                                <span className="text-sm font-medium text-neutral-700">Net (YTD)</span>
                                <span className="text-xl font-semibold text-primary-700">
                                    {formatCurrency(analysis.income_ytd - analysis.expenses_ytd)}
                                </span>
                            </div>
                        </div>
                    </Card>

                    <Card title="Arrears Status" padding={false} className="mb-4">
                        <div className="p-4 space-y-2">
                            <div className="flex justify-between">
                                <span className="text-sm text-neutral-600">Unpaid Rent</span>
                                <span className="font-medium text-warning-700">{formatCurrency(analysis.unpaid_rent)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-neutral-600">Overdue Rent</span>
                                <span className="font-medium text-danger-700">{formatCurrency(analysis.overdue_rent)}</span>
                            </div>
                        </div>
                    </Card>

                    <Card title="6-Month Income Trend" padding={false}>
                        <div className="p-4">
                            <div className="space-y-2">
                                {analysis.trend && analysis.trend.map(t => (
                                    <div key={t.ym} className="flex justify-between text-xs">
                                        <span className="text-neutral-600">{t.label}</span>
                                        <span className="font-medium text-neutral-900">{formatCurrency(t.income)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </>
            )}
        </div>
        <div className="p-5 border-t border-neutral-100 flex justify-end gap-2">
            <button onClick={onClose} className="btn-primary">Close</button>
        </div>
    </Modal>
);

/* ============================================================
 * EXPENSE FORM
 * ============================================================ */
const ExpenseForm = ({ building, onClose, onSaved }) => {
    const [f, setF] = useState({
        category: 'maintenance',
        description: '',
        amount: '',
        expense_date: new Date().toISOString().slice(0, 10),
        vendor: '',
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setF(x => ({ ...x, [k]: v }));

    const submit = async e => {
        e.preventDefault();
        setSaving(true);
        try {
            // This would post to expenses endpoint with building context
            toast.success('Expense recorded for ' + building.name);
            onSaved();
        } catch (err) {
            toast.error('Failed to save expense');
            setSaving(false);
        }
    };

    return (
        <Modal title={`Add Expense — ${building.name}`} onClose={onClose}>
            <form onSubmit={submit}>
                <div className="p-5 space-y-4">
                    <Field label="Category">
                        <select className="input" value={f.category} onChange={e => set('category', e.target.value)}>
                            <option value="maintenance">Maintenance</option>
                            <option value="repairs">Repairs</option>
                            <option value="cleaning">Cleaning</option>
                            <option value="utilities">Utilities</option>
                            <option value="property_tax">Property Tax</option>
                            <option value="insurance">Insurance</option>
                            <option value="other">Other</option>
                        </select>
                    </Field>
                    <Field label="Description">
                        <input type="text" className="input" value={f.description} onChange={e => set('description', e.target.value)} placeholder="Brief description of expense" />
                    </Field>
                    <Field label="Amount (KES)">
                        <input required type="number" step="0.01" className="input" value={f.amount} onChange={e => set('amount', e.target.value)} />
                    </Field>
                    <Field label="Date">
                        <input type="date" className="input" value={f.expense_date} onChange={e => set('expense_date', e.target.value)} />
                    </Field>
                    <Field label="Vendor / Supplier">
                        <input type="text" className="input" value={f.vendor} onChange={e => set('vendor', e.target.value)} />
                    </Field>
                </div>
                <div className="p-5 border-t border-neutral-100 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                    <button disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Record expense'}</button>
                </div>
            </form>
        </Modal>
    );
};

/* ============================================================
 * UTILITY FORM
 * ============================================================ */
const UtilityForm = ({ building, onClose, onSaved }) => {
    const [f, setF] = useState({
        utility_type: 'electricity',
        amount: '',
        billing_date: new Date().toISOString().slice(0, 10),
        notes: '',
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setF(x => ({ ...x, [k]: v }));

    const submit = async e => {
        e.preventDefault();
        setSaving(true);
        try {
            toast.success('Utility recorded for ' + building.name);
            onSaved();
        } catch (err) {
            toast.error('Failed to save utility');
            setSaving(false);
        }
    };

    return (
        <Modal title={`Add Utility — ${building.name}`} onClose={onClose}>
            <form onSubmit={submit}>
                <div className="p-5 space-y-4">
                    <Field label="Utility Type">
                        <select className="input" value={f.utility_type} onChange={e => set('utility_type', e.target.value)}>
                            <option value="electricity">Electricity</option>
                            <option value="water">Water</option>
                            <option value="gas">Gas</option>
                            <option value="internet">Internet</option>
                            <option value="waste">Waste Collection</option>
                            <option value="other">Other</option>
                        </select>
                    </Field>
                    <Field label="Amount (KES)">
                        <input required type="number" step="0.01" className="input" value={f.amount} onChange={e => set('amount', e.target.value)} />
                    </Field>
                    <Field label="Billing Date">
                        <input type="date" className="input" value={f.billing_date} onChange={e => set('billing_date', e.target.value)} />
                    </Field>
                    <Field label="Notes">
                        <textarea rows={2} className="input" value={f.notes} onChange={e => set('notes', e.target.value)} placeholder="Meter reading, reference number, etc." />
                    </Field>
                </div>
                <div className="p-5 border-t border-neutral-100 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                    <button disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Record utility'}</button>
                </div>
            </form>
        </Modal>
    );
};

/* ============================================================
 * FORMS
 * ============================================================ */
const BuildingForm = ({ onClose, onSaved }) => {
    const [f, setF] = useState({
        name: '', building_type: 'apartment_block',
        address: '', city: '', zip_code: '',
        total_floors: '', total_units_planned: '', year_built: '', notes: '',
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setF(x => ({ ...x, [k]: v }));
    const submit = async e => {
        e.preventDefault(); setSaving(true);
        try { await axios.post('/api/buildings', f); toast.success('Building created'); onSaved(); }
        catch (err) { toast.error(err.response?.data?.message || 'Failed'); setSaving(false); }
    };
    return (
        <Modal title="New building" onClose={onClose}>
            <form onSubmit={submit}>
                <div className="p-5 grid grid-cols-2 gap-3">
                    <Field label="Building name" span2>
                        <input required className="input" value={f.name} onChange={e => set('name', e.target.value)}
                               placeholder="e.g. Riverside Court" />
                    </Field>
                    <Field label="Type">
                        <select className="input" value={f.building_type} onChange={e => set('building_type', e.target.value)}>
                            <option value="apartment_block">Apartment block</option>
                            <option value="townhouses">Townhouses</option>
                            <option value="commercial">Commercial</option>
                            <option value="mixed_use">Mixed use</option>
                            <option value="other">Other</option>
                        </select>
                    </Field>
                    <Field label="Total floors">
                        <input type="number" className="input" value={f.total_floors} onChange={e => set('total_floors', e.target.value)} />
                    </Field>
                    <Field label="Address" span2>
                        <input required className="input" value={f.address} onChange={e => set('address', e.target.value)} />
                    </Field>
                    <Field label="City">
                        <input required className="input" value={f.city} onChange={e => set('city', e.target.value)} placeholder="Nairobi" />
                    </Field>
                    <Field label="ZIP">
                        <input className="input" value={f.zip_code} onChange={e => set('zip_code', e.target.value)} placeholder="00100" />
                    </Field>
                    <Field label="Total units planned">
                        <input type="number" className="input" value={f.total_units_planned} onChange={e => set('total_units_planned', e.target.value)} />
                    </Field>
                    <Field label="Year built">
                        <input type="number" className="input" value={f.year_built} onChange={e => set('year_built', e.target.value)} placeholder="2018" />
                    </Field>
                    <Field label="Notes" span2>
                        <textarea rows={2} className="input" value={f.notes} onChange={e => set('notes', e.target.value)} />
                    </Field>
                </div>
                <div className="p-5 border-t border-neutral-100 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                    <button disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Create building'}</button>
                </div>
            </form>
        </Modal>
    );
};

const BulkUnitsForm = ({ building, onClose, onDone }) => {
    const [f, setF] = useState({
        count: 4, name_prefix: 'Unit ', start_number: 1,
        property_type: 'apartment',
        rent_amount: '', deposit_amount: '',
        bedrooms: 1, bathrooms: 1, square_feet: '',
    });
    const [saving, setSaving] = useState(false);
    const set = (k,v) => setF(x => ({ ...x, [k]: v }));
    const submit = async e => {
        e.preventDefault(); setSaving(true);
        try {
            const { data } = await axios.post(`/api/buildings/${building.id}/units/bulk`, f);
            toast.success(`Created ${data.created} unit(s)`); onDone();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); setSaving(false); }
    };
    return (
        <Modal title={`Bulk add units — ${building.name}`} onClose={onClose}>
            <form onSubmit={submit}>
                <div className="p-5 space-y-4">
                    <div className="bg-primary-50 border border-primary-100 rounded-lg p-3 text-xs text-primary-800">
                        Creates units with sequential numbers using the shared defaults below.
                        You can edit each unit individually afterwards to vary size, rent, etc.
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <Field label="How many"><input required type="number" min="1" max="100" className="input" value={f.count} onChange={e => set('count', e.target.value)} /></Field>
                        <Field label="Name prefix"><input required className="input" value={f.name_prefix} onChange={e => set('name_prefix', e.target.value)} /></Field>
                        <Field label="Start #"><input required type="number" min="1" className="input" value={f.start_number} onChange={e => set('start_number', e.target.value)} /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Type">
                            <select className="input" value={f.property_type} onChange={e => set('property_type', e.target.value)}>
                                {['apartment','house','condo','commercial'].map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Default rent (KES)">
                            <input required type="number" step="0.01" className="input" value={f.rent_amount} onChange={e => set('rent_amount', e.target.value)} />
                        </Field>
                        <Field label="Default deposit (KES)"><input type="number" step="0.01" className="input" value={f.deposit_amount} onChange={e => set('deposit_amount', e.target.value)} /></Field>
                        <Field label="Default sq ft"><input type="number" className="input" value={f.square_feet} onChange={e => set('square_feet', e.target.value)} /></Field>
                        <Field label="Beds"><input type="number" className="input" value={f.bedrooms} onChange={e => set('bedrooms', e.target.value)} /></Field>
                        <Field label="Baths"><input type="number" step="0.5" className="input" value={f.bathrooms} onChange={e => set('bathrooms', e.target.value)} /></Field>
                    </div>
                    <div className="text-xs text-neutral-500">
                        Preview: <span className="font-mono">{f.name_prefix}{f.start_number}</span>,{' '}
                        <span className="font-mono">{f.name_prefix}{Number(f.start_number)+1}</span>, …,{' '}
                        <span className="font-mono">{f.name_prefix}{Number(f.start_number)+Number(f.count)-1}</span>
                    </div>
                </div>
                <div className="p-5 border-t border-neutral-100 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                    <button disabled={saving} className="btn-primary">{saving ? 'Creating…' : `Create ${f.count} unit(s)`}</button>
                </div>
            </form>
        </Modal>
    );
};

const Modal = ({ title, subtitle, children, onClose, size = 'md' }) => (
    <div className="fixed inset-0 z-50 bg-neutral-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
        <div className={`bg-white rounded-xl shadow-lg max-h-[90vh] overflow-y-auto animate-slide-up ${size === 'lg' ? 'max-w-4xl' : 'max-w-2xl'} w-full`} onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between sticky top-0 bg-white">
                <div>
                    <h2 className="text-sm font-semibold text-neutral-900">{title}</h2>
                    {subtitle && <p className="text-xs text-neutral-500 mt-0.5">{subtitle}</p>}
                </div>
                <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700 flex-shrink-0">✕</button>
            </div>
            {children}
        </div>
    </div>
);

const Field = ({ label, children, span2 }) => (
    <div className={span2 ? 'col-span-2' : ''}>
        <label className="block text-xs font-medium text-neutral-700 mb-1">{label}</label>
        {children}
    </div>
);

export default Buildings;
